import type { ViewMode } from '@/state/appState';

const HEAT_STOPS: Array<[number, [number, number, number]]> = [
  [0.0, [10, 12, 40]],
  [0.25, [60, 40, 140]],
  [0.5, [190, 60, 90]],
  [0.75, [245, 150, 40]],
  [1.0, [255, 250, 200]],
];

export function heatColor(t: number): [number, number, number] {
  const v = Math.max(0, Math.min(1, t));
  for (let i = 1; i < HEAT_STOPS.length; i++) {
    const [t1, c1] = HEAT_STOPS[i];
    if (v <= t1) {
      const [t0, c0] = HEAT_STOPS[i - 1];
      const f = (v - t0) / (t1 - t0);
      return [c0[0] + (c1[0] - c0[0]) * f, c0[1] + (c1[1] - c0[1]) * f, c0[2] + (c1[2] - c0[2]) * f];
    }
  }
  return HEAT_STOPS[HEAT_STOPS.length - 1][1];
}

export interface ViewSources {
  original: Uint8Array | null;
  processed: Uint8Array | null;
  current: Uint8Array | null;
  width: number;
  height: number;
}

function gray(data: Uint8Array, width: number, height: number): ImageData {
  const out = new Uint8ClampedArray(width * height * 4);
  for (let i = 0, j = 0; i < data.length; i++, j += 4) {
    out[j] = out[j + 1] = out[j + 2] = data[i];
    out[j + 3] = 255;
  }
  return new ImageData(out, width, height);
}

export function differenceImage(processed: Uint8Array, current: Uint8Array, width: number, height: number): ImageData {
  const out = new Uint8ClampedArray(width * height * 4);
  for (let i = 0, j = 0; i < processed.length; i++, j += 4) {
    const targetDark = 255 - processed[i];
    const currentDark = 255 - current[i];
    const v = 128 - (targetDark - currentDark) / 2;
    out[j] = out[j + 1] = out[j + 2] = v;
    out[j + 3] = 255;
  }
  return new ImageData(out, width, height);
}

export function heatmapImage(processed: Uint8Array, current: Uint8Array, width: number, height: number): ImageData {
  const out = new Uint8ClampedArray(width * height * 4);
  for (let i = 0, j = 0; i < processed.length; i++, j += 4) {
    const diff = Math.abs(processed[i] - current[i]) / 255;
    const [r, g, b] = heatColor(diff);
    out[j] = r;
    out[j + 1] = g;
    out[j + 2] = b;
    out[j + 3] = 255;
  }
  return new ImageData(out, width, height);
}

/** Builds the underlay image for a view mode, or `null` when unavailable. */
export function buildViewImage(mode: ViewMode, sources: ViewSources): ImageData | null {
  const { width, height } = sources;
  if (width === 0 || height === 0) return null;
  switch (mode) {
    case 'strings':
      return null;
    case 'original':
      return sources.original ? gray(sources.original, width, height) : null;
    case 'processed':
      return sources.processed ? gray(sources.processed, width, height) : null;
    case 'approximation':
      return sources.current ? gray(sources.current, width, height) : null;
    case 'difference':
      return sources.processed && sources.current ? differenceImage(sources.processed, sources.current, width, height) : null;
    case 'heatmap':
      return sources.processed && sources.current ? heatmapImage(sources.processed, sources.current, width, height) : null;
    default:
      return null;
  }
}

/** Mean absolute error between two lightness buffers, in [0, 1]. */
export function meanAbsoluteError(a: Uint8Array, b: Uint8Array): number {
  if (a.length === 0 || a.length !== b.length) return 0;
  let sum = 0;
  for (let i = 0; i < a.length; i++) sum += Math.abs(a[i] - b[i]);
  return sum / (a.length * 255);
}
